import React, { useState } from "react";
import { Info } from "lucide-react";
import "./LandingResearchPreview.css";

const MODEL_CHIPS = [
  { name: "ChatGPT", color: "#10a37f", on: true },
  { name: "Claude", color: "#d97757", on: true },
  { name: "Gemini", color: "#4285f4", on: true },
  { name: "Perplexity", color: "#20808d", on: true },
  { name: "Grok", color: "#555555", on: false },
];

const AI_CARDS = [
  {
    name: "ChatGPT",
    color: "#10a37f",
    sections: [
      {
        title: "I. Issue(s) Presented",
        bullets: [
          "Identifies core legal issue and cure window applicability",
          "Flags whether buyer's continued acceptance waives strict timing",
        ],
      },
      {
        title: "II. Relevant Rules / Legal Principles",
        bullets: [
          "Substantial performance doctrine under Restatement §237",
          "Jurisdiction-specific treatment of time-of-essence clauses",
        ],
      },
    ],
  },
  {
    name: "Claude",
    color: "#d97757",
    sections: [
      {
        title: "I. Issue(s) Presented",
        bullets: [
          "Examines whether tendered cure defeats material breach element",
          "Analyzes UCC §2-508 cure rights against TOE clause enforceability",
        ],
      },
      {
        title: "II. Relevant Rules / Legal Principles",
        bullets: [
          "Statutory cure rights and interaction with express TOE terms",
          "Mitigation obligations for non-breaching party post-tender",
        ],
      },
    ],
  },
  {
    name: "Gemini",
    color: "#4285f4",
    sections: [
      {
        title: "I. Issue(s) Presented",
        bullets: [
          "Frames dispute around jurisdictional standard for cure window",
          "Identifies circuit split as central strategic variable",
        ],
      },
      {
        title: "II. Relevant Rules / Legal Principles",
        bullets: [
          "2nd Circuit strict enforcement vs. 9th Circuit totality standard",
          "Estoppel as alternative path where acceptance continued",
        ],
      },
    ],
  },
  {
    name: "Perplexity",
    color: "#20808d",
    sections: [
      {
        title: "I. Issue(s) Presented",
        bullets: [
          "Anchors analysis in recent caselaw trend on cure enforcement",
          "Tests whether cure was timely and commercially reasonable",
        ],
      },
      {
        title: "II. Relevant Rules / Legal Principles",
        bullets: [
          "Cure-tender doctrine with citation to recent circuit opinions",
          "Trend toward enforcement where cure was commercially reasonable",
        ],
      },
    ],
  },
];

const LandingResearchPreview = () => {
  const [activeTab, setActiveTab] = useState("comp");

  return (
    <section className="landing-research-section">
      <div className="landing-research-inner">
        <p className="landing-research-label">Research Mode</p>
        <h2 className="landing-research-title">
          How Multiple AI Opinions Become One Clear Strategy.
        </h2>
        <p className="landing-research-sub">
          Ask a legal question or upload a brief. LawFusion compares multiple AI
          analyses, highlights disagreement, and synthesizes the strongest path
          forward.
        </p>

        <div className="landing-research-preview">
          <div className="landing-research-topbar">
            <span className="landing-research-dot landing-research-dot-r" />
            <span className="landing-research-dot landing-research-dot-y" />
            <span className="landing-research-dot landing-research-dot-g" />
            <div className="landing-research-topbar-right">
              <span className="landing-research-topbar-label">
                Research Mode Preview
              </span>
              <span className="landing-research-topbar-badge">Sample Output</span>
            </div>
          </div>

          <div className="landing-research-query-row">
            <div className="landing-research-query-lbl">Research Query</div>
            <div className="landing-research-query-text">
              Analyze the strongest arguments, key vulnerabilities, and missing
              facts in a breach-of-contract dispute involving late delivery and
              partial cure.
            </div>
          </div>

          <div className="landing-research-models-row">
            <span className="landing-research-models-lbl">AI Models</span>
            {MODEL_CHIPS.map((chip) => (
              <span
                key={chip.name}
                className={`landing-research-chip${chip.on ? " is-on" : " is-off"}`}
              >
                <span
                  className="landing-research-cdot"
                  style={{ background: chip.color }}
                />
                {chip.name}
              </span>
            ))}
            <span className="landing-research-models-count">4/5 selected</span>
          </div>

          <div className="landing-research-cards-grid">
            {AI_CARDS.map((card) => (
              <div key={card.name} className="landing-research-ai-card">
                <div
                  className="landing-research-ai-card-title"
                  style={{ color: card.color }}
                >
                  <span
                    className="landing-research-mdot"
                    style={{ background: card.color }}
                  />
                  {card.name}&apos;s Response Analysis
                </div>
                {card.sections.map((section) => (
                  <React.Fragment key={section.title}>
                    <div className="landing-research-sh">{section.title}</div>
                    {section.bullets.map((bullet) => (
                      <div key={bullet} className="landing-research-bul">
                        {bullet}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
                <div className="landing-research-card-fade-wrap">
                  <div className="landing-research-card-fade" />
                  <span className="landing-research-more-cue">
                    Full analysis continues
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="landing-research-tab-bar" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "comp"}
              className={`landing-research-tab${activeTab === "comp" ? " is-active" : ""}`}
              onClick={() => setActiveTab("comp")}
            >
              Comparative Analysis
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "synth"}
              className={`landing-research-tab${activeTab === "synth" ? " is-active" : ""}`}
              onClick={() => setActiveTab("synth")}
            >
              Final Synthesis
            </button>
          </div>

          {activeTab === "comp" && (
            <div className="landing-research-tab-prompt">
              <button
                type="button"
                className="landing-research-tab-prompt-btn"
                onClick={() => setActiveTab("synth")}
              >
                → Click Final Synthesis to see how LawFusion synthesizes the
                strongest path forward
              </button>
            </div>
          )}

          {activeTab === "comp" && (
            <div className="landing-research-tab-panel" role="tabpanel">
              <div className="landing-research-comp-panel">
                <div className="landing-research-comp-header">
                  <span className="landing-research-comp-title">
                    Comparative Analysis
                  </span>
                  <span className="landing-research-comp-meta">
                    4 models compared
                  </span>
                </div>

                <div className="landing-research-comp-section">
                  <div className="landing-research-comp-section-title">
                    <span className="landing-research-ctag landing-research-tag-agree">
                      Areas of Agreement
                    </span>
                  </div>
                  <div className="landing-research-comp-bul agree">
                    All four models identify substantial performance as the
                    strongest defensive theory
                  </div>
                  <div className="landing-research-comp-bul agree">
                    Unanimous view that a properly documented cure tender defeats
                    the material-breach element
                  </div>
                </div>

                <div className="landing-research-comp-section">
                  <div className="landing-research-comp-section-title">
                    <span className="landing-research-ctag landing-research-tag-disagree">
                      Areas of Disagreement
                    </span>
                  </div>
                  <div className="landing-research-comp-bul disagree">
                    ChatGPT and Claude emphasize the contractual cure window;
                    Gemini centers jurisdictional split
                  </div>
                  <div className="landing-research-comp-bul disagree">
                    Models split on whether UCC §2-508 or common-law cure
                    doctrine controls
                  </div>
                </div>

                <div className="landing-research-comp-section">
                  <div className="landing-research-comp-section-title">
                    <span className="landing-research-ctag landing-research-tag-quality">
                      Reasoning Quality Differences
                    </span>
                  </div>
                  <div className="landing-research-comp-bul quality">
                    ChatGPT and Claude rely on fact-specific framing; Gemini
                    anchors in circuit-level doctrine
                  </div>
                  <div className="landing-research-comp-bul quality">
                    Perplexity&apos;s analysis is citation-heavy but less tested
                    at trial-court level
                  </div>
                </div>

                <div className="landing-research-comp-section">
                  <div className="landing-research-comp-section-title">
                    <span className="landing-research-ctag landing-research-tag-missing">
                      Missing Facts Identified
                    </span>
                  </div>
                  <div className="landing-research-comp-bul missing">
                    Final venue and forum-selection clause not yet established
                  </div>
                  <div className="landing-research-comp-bul missing">
                    Plaintiff&apos;s post-cure mitigation log absent from record
                  </div>
                  <div className="landing-research-comp-bul missing">
                    Predominant-purpose evidence needed to confirm UCC
                    applicability
                  </div>
                </div>

                <div className="landing-research-comp-section landing-research-comp-last">
                  <div className="landing-research-comp-section-title">
                    <span className="landing-research-ctag landing-research-tag-vuln">
                      Critical Vulnerabilities (Ranked)
                    </span>
                  </div>
                  <div className="landing-research-comp-bul vuln">
                    TOE Clause Strictness — CRITICAL: express TOE may override
                    substantial performance in strict jurisdictions
                  </div>
                  <div className="landing-research-comp-bul vuln">
                    UCC Applicability — HIGH: service-dominant reading narrows
                    cure rights significantly
                  </div>
                  <div className="landing-research-comp-bul vuln">
                    Cure Documentation — MODERATE: absence of written tender
                    record weakens position on appeal
                  </div>
                  <div className="landing-research-more-cue-tab">
                    Full comparative analysis continues
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "synth" && (
            <div className="landing-research-tab-panel" role="tabpanel">
              <div className="landing-research-synth-panel">
                <div className="landing-research-synth-header">
                  <span className="landing-research-synth-title">
                    Final Synthesis
                  </span>
                  <span className="landing-research-strong-badge">
                    Position: STRONG
                  </span>
                </div>

                <div className="landing-research-ss">
                  <div className="landing-research-ss-title">
                    1. Issue(s) Presented
                  </div>
                  <div className="landing-research-sb">
                    Core legal issue identified and confirmed across all four
                    models
                  </div>
                  <div className="landing-research-sb">
                    Cure window applicability established as the central dispute
                  </div>
                </div>

                <div className="landing-research-ss">
                  <div className="landing-research-ss-title">
                    2. Relevant Rules / Legal Principles
                  </div>
                  <div className="landing-research-sb">
                    Governing doctrine identified with jurisdictional variation
                    noted
                  </div>
                  <div className="landing-research-sb">
                    Statutory and common-law cure rights synthesized into unified
                    framework
                  </div>
                </div>

                <div className="landing-research-ss">
                  <div className="landing-research-ss-title">
                    3. Application / Analysis
                  </div>
                  <div className="landing-research-sb">
                    Models converge on strongest defensive theory with supporting
                    rationale
                  </div>
                  <div className="landing-research-sb">
                    Key points of disagreement between models flagged for
                    attorney review
                  </div>
                </div>

                <div className="landing-research-ss">
                  <div className="landing-research-ss-title">
                    4. Vulnerability Analysis
                  </div>
                  <div className="landing-research-sb is-vuln">
                    Primary vulnerability identified — litigation consequence
                    explained
                  </div>
                  <div className="landing-research-sb is-vuln">
                    Corrective action recommended before opposing counsel
                    exploits gap
                  </div>
                  <div className="landing-research-sb is-vuln">
                    Secondary exposure flagged with suggested mitigation path
                  </div>
                </div>

                <div className="landing-research-ss">
                  <div className="landing-research-ss-title">
                    5. Important Next Steps
                  </div>
                  <div className="landing-research-sb">
                    Strengthen record on cure timing and written tender
                  </div>
                  <div className="landing-research-sb">
                    Confirm governing law before relying on UCC cure theory
                  </div>
                  <div className="landing-research-more-cue-tab">
                    Full synthesis continues
                  </div>
                </div>

                <div className="landing-research-synth-fade" />
              </div>
            </div>
          )}
        </div>

        <p className="landing-research-disclaimer">
          <span>
            <Info size={13} aria-hidden="true" />
            Preview only — full outputs include complete model responses,
            detailed comparative analysis, ranked vulnerabilities, and final
            synthesis.
          </span>
        </p>
      </div>
    </section>
  );
};

export default LandingResearchPreview;
